import Papa, { ParseConfig } from "papaparse";
import { Country } from "./Country";
import { AllHtmlEntities } from "html-entities";

export interface DataItem {
  saleMonth: Date;
  store: string;
  country: string;
  isrc: string;
  artist: string;
  title: string;
  releaseType: string;
  quantity: number;
  earningsUSD: number;
}

export interface MonthTotalDataItem {
  month: Date;
  quantity: number;
  earningsUSD: number;
}

export interface CountryTotalDataItem {
  country: string;
  quantity: number;
  earningsUSD: number;
}

export interface TopDataItem {
  id: string;
  name: string;
  value: number;
  monthlyTotals?: MonthTotalDataItem[];
}

export interface Filter {
  fromDate?: Date;
  toDate?: Date;
  isrc?: string;
  artist?: string;
  country?: string;
  store?: string;
}

export interface NameValuePair {
  value: string;
  name: string;
}

export type Order = "asc" | "desc";

export class DistroDataSet {
  public rawDataSet?: DataItem[];

  public get length(): number {
    return this.rawDataSet !== undefined ? this.rawDataSet.length : 0;
  }

  public get minMonth(): Date | undefined {
    let result: Date | undefined = undefined;
    if (this.rawDataSet !== undefined) {
      this.rawDataSet.forEach(di => {
        if (result === undefined || di.saleMonth < result) {
          result = di.saleMonth;
        }
      });
    }

    return result;
  }

  public get maxMonth(): Date | undefined {
    let result: Date | undefined = undefined;
    if (this.rawDataSet !== undefined) {
      this.rawDataSet.forEach(di => {
        if (result === undefined || di.saleMonth > result) {
          result = di.saleMonth;
        }
      });
    }

    return result;
  }

  private itemMatchesFilter(item: DataItem, filter: Filter): boolean {
    if (
      (filter.isrc === undefined || filter.isrc === item.isrc) &&
      (filter.country === undefined || filter.country === item.country) &&
      (filter.store === undefined || filter.store === item.store) &&
      (filter.artist === undefined || filter.artist === item.artist)
    ) {
      return true;
    } else {
      return false;
    }
  }

  private filterFromDate?: Date;
  private filterToDate?: Date;
  private dateFilteredData?: DataItem[];
  private getFilteredRawData(fromDate?: Date, toDate?: Date): DataItem[] {
    if (
      this.dateFilteredData !== undefined &&
      this.filterFromDate === fromDate &&
      this.filterToDate === toDate
    ) {
      return this.dateFilteredData;
    } else {
      let result: DataItem[] = [];
      if (this.rawDataSet && this.rawDataSet.length > 0) {
        // filter by date
        result = this.rawDataSet
          .filter(di => {
            let filterOut = false;
            if (
              fromDate !== undefined &&
              di.saleMonth !== undefined &&
              di.saleMonth < fromDate
            ) {
              filterOut = true;
            }
            if (
              toDate !== undefined &&
              di.saleMonth !== undefined &&
              di.saleMonth > toDate
            ) {
              filterOut = true;
            }
            // filter referral revenue out
            if (di.store === "DistroKid Referral") {
              filterOut = true;
            }
            return !filterOut ? di : undefined;
          })
          .sort((di1, di2) => (di1.saleMonth < di2.saleMonth ? 1 : -1));
      }
      this.dateFilteredData = result;
      return result;
    }
  }

  public getMonthTotals(filter: Filter): MonthTotalDataItem[] {
    const result: MonthTotalDataItem[] = [];
    const filteredDataSet = this.getFilteredRawData(
      filter.fromDate,
      filter.toDate
    );
    if (filteredDataSet && filteredDataSet.length > 0) {
      // get totals
      filteredDataSet.forEach(di => {
        if (this.itemMatchesFilter(di, filter)) {
          const monthTotal = result.find(mt => mt.month === di.saleMonth);
          if (monthTotal !== undefined) {
            monthTotal.quantity += di.quantity;
            monthTotal.earningsUSD += di.earningsUSD;
          } else if (di.saleMonth !== undefined) {
            result.push({
              month: di.saleMonth,
              quantity: di.quantity,
              earningsUSD: di.earningsUSD
            });
          }
        }
      });
    }
    return result.sort((di1, di2) => {
      return di1.month > di2.month ? 1 : -1;
    });
  }

  public getTotalEarnings(filter: Filter): number {
    let result = 0;
    const filteredDataSet = this.getFilteredRawData(
      filter.fromDate,
      filter.toDate
    );
    if (filteredDataSet && filteredDataSet.length > 0) {
      // get totals
      filteredDataSet.forEach(di => {
        if (this.itemMatchesFilter(di, filter)) {
          result += di.earningsUSD;
        }
      });
    }
    return result;
  }

  public getCountryTotals(filter: Filter): CountryTotalDataItem[] {
    const result: CountryTotalDataItem[] = [];
    const filteredDataSet = this.getFilteredRawData(
      filter.fromDate,
      filter.toDate
    );
    if (filteredDataSet && filteredDataSet.length > 0) {
      // get totals
      filteredDataSet.forEach(di => {
        if (this.itemMatchesFilter(di, filter)) {
          const countryTotal = result.find(mt => mt.country === di.country);
          if (countryTotal !== undefined) {
            countryTotal.quantity += di.quantity;
            countryTotal.earningsUSD += di.earningsUSD;
          } else if (di.country !== undefined) {
            result.push({
              country: di.country,
              quantity: di.quantity,
              earningsUSD: di.earningsUSD
            });
          }
        }
      });
    }
    return result;
  }

  public getTopByRevenue(
    field: string,
    idField: string,
    filter: Filter,
    count = 5,
    order: Order = "desc"
  ): TopDataItem[] {
    const result: TopDataItem[] = [];
    const filteredDataSet = this.getFilteredRawData(
      filter.fromDate,
      filter.toDate
    );
    if (filteredDataSet && filteredDataSet.length > 0) {
      // get totals
      filteredDataSet.forEach(di => {
        if (this.itemMatchesFilter(di, filter)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const item = result.find(top => top.name === (di as any)[field]);
          if (item !== undefined) {
            item.value += di.earningsUSD;
          } else if (di.saleMonth !== undefined) {
            result.push({
              /* eslint-disable @typescript-eslint/no-explicit-any */
              id: (di as any)[idField],
              name: (di as any)[field],
              /* eslint-enable @typescript-eslint/no-explicit-any */
              value: di.earningsUSD
            });
          }
        }
      });
    }
    return result
      .sort((di1, di2) => {
        if (order === "desc") {
          return di1.value < di2.value ? 1 : -1;
        } else {
          return di1.value > di2.value ? 1 : -1;
        }
      })
      .slice(0, count);
  }

  public getTopReleasesByRevenue(
    filter: Filter,
    count = 5,
    order: Order = "desc"
  ): TopDataItem[] {
    return this.getTopByRevenue("title", "isrc", filter, count, order);
  }

  public getTopStoresByRevenue(filter: Filter): TopDataItem[] {
    return this.getTopByRevenue("store", "store", filter);
  }

  public getTopCountriesByRevenue(filter: Filter): TopDataItem[] {
    const result = this.getTopByRevenue("country", "country", filter);
    result.forEach(c => {
      c.name = Country.getCountryByCode(c.id)?.name || c.name;
    });
    return result;
  }

  public getTopArtistsByRevenue(filter: Filter): TopDataItem[] {
    return this.getTopByRevenue("artist", "artist", filter);
  }

  public getUniqueValues(field: string, filter: Filter): string[] {
    const result: string[] = [];

    const filteredDataSet = this.getFilteredRawData(
      filter.fromDate,
      filter.toDate
    );
    if (filteredDataSet && filteredDataSet.length > 0) {
      // get totals
      filteredDataSet.forEach(di => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const value = (di as any)[field];
        if (
          value !== undefined &&
          this.itemMatchesFilter(di, filter) &&
          result.indexOf(value) === -1
        ) {
          result.push(value);
        }
      });
    }
    return result;
  }

  public getUniqueNameValues(
    valueField: string,
    nameField: string,
    filter: Filter
  ): NameValuePair[] {
    const result: NameValuePair[] = [];

    const filteredDataSet = this.getFilteredRawData(
      filter.fromDate,
      filter.toDate
    );
    if (filteredDataSet && filteredDataSet.length > 0) {
      // get totals
      filteredDataSet.forEach(di => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const value = (di as any)[valueField];
        if (
          value !== undefined &&
          this.itemMatchesFilter(di, filter) &&
          result.findIndex(v => v.value === value) === -1
        ) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          result.push({ name: (di as any)[nameField], value: value });
        }
      });
    }
    return result;
  }

  private transformDataHeader(header: string): string {
    switch (header) {
      case "Sale Month":
        return "saleMonth";
      case "Store":
        return "store";
      case "Country of Sale":
        return "country";
      case "ISRC":
        return "isrc";
      case "Artist":
        return "artist";
      case "Title":
        return "title";
      case "Song/Album":
        return "releaseType";
      case "Quantity":
        return "quantity";
      case "Earnings (USD)":
        return "earningsUSD";
      default:
        return header;
    }
  }

  parseConfig: ParseConfig = {
    header: true,
    dynamicTyping: true,
    delimiter: "\t",
    transformHeader: this.transformDataHeader
  };

  public static async createFromDataFile(
    file: File | string
  ): Promise<DistroDataSet> {
    const result = new DistroDataSet();
    if (!(file instanceof File)) {
      result.parseConfig.download = true;
    }

    result.rawDataSet = await result.parseFile(file, result.parseConfig);
    return result;
  }

  private parseFile(
    dataFile: File | string,
    config: ParseConfig
  ): Promise<DataItem[]> {
    return new Promise<DataItem[]>((resolve, reject) => {
      const parsedData: DataItem[] = [];
      const entities = new AllHtmlEntities();
      if (
        !(dataFile instanceof File) ||
        (dataFile instanceof File && dataFile.name.endsWith(".tsv"))
      ) {
        config.step = result => {
          const rawDataItem = (result.data as unknown) as DataItem;
          if (rawDataItem.isrc && rawDataItem.isrc.length > 0) {
            parsedData.push({
              saleMonth: rawDataItem.saleMonth,
              isrc: rawDataItem.isrc,
              artist: entities.decode(rawDataItem.artist),
              title: entities.decode(rawDataItem.title),
              releaseType: rawDataItem.releaseType,
              store: rawDataItem.store,
              country: rawDataItem.country,
              quantity: rawDataItem.quantity,
              earningsUSD: rawDataItem.earningsUSD
            });
          }
        };
        config.complete = () => {
          resolve(parsedData);
        };
        Papa.parse(dataFile, config);
      } else {
        reject(new Error("Wrong file type."));
      }
    });
  }
}
